import { validatePuzzle } from './schema.js';
/** Character that marks a black/block square in a layout row. */
export const BLOCK = '#';
/** Normalize a layout row: strip spaces, uppercase. */
function normalizeRow(row) {
    return row.replace(/\s+/g, '').toUpperCase();
}
/** Parse a layout into a rectangular char grid; throws if ragged or empty. */
function parseLayout(layout) {
    if (layout.length === 0)
        throw new Error('layout has no rows');
    const rows = layout.map(normalizeRow);
    const width = rows[0].length;
    if (width === 0)
        throw new Error('layout row 0 is empty');
    rows.forEach((r, i) => {
        if (r.length !== width) {
            throw new Error(`layout row ${i} has width ${r.length}, expected ${width}`);
        }
        if (!/^[A-Z#]+$/.test(r)) {
            throw new Error(`layout row ${i} has illegal characters: ${JSON.stringify(r)}`);
        }
    });
    return rows.map((r) => r.split(''));
}
const isWhite = (ch) => ch !== undefined && ch !== BLOCK;
/**
 * Derive the numbered grid and the ordered list of slots from a char grid.
 *
 * American numbering: a cell is numbered iff it starts an across word (no white
 * cell to its left, a white cell to its right) and/or a down word (no white
 * cell above, a white cell below). Numbers run reading order from 1.
 */
export function deriveSlots(chars) {
    const rows = chars.length;
    const cols = chars[0].length;
    const ch = (r, c) => chars[r]?.[c];
    const grid = chars.map((row) => row.map((cell) => cell === BLOCK
        ? { block: true }
        : { block: false, solution: cell, number: null }));
    const slots = [];
    let next = 1;
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (!isWhite(ch(r, c)))
                continue;
            const startsAcross = !isWhite(ch(r, c - 1)) && isWhite(ch(r, c + 1));
            const startsDown = !isWhite(ch(r - 1, c)) && isWhite(ch(r + 1, c));
            if (!startsAcross && !startsDown)
                continue;
            const number = next;
            next += 1;
            grid[r][c].number = number;
            if (startsAcross) {
                let len = 0;
                let answer = '';
                while (isWhite(ch(r, c + len))) {
                    answer += ch(r, c + len);
                    len += 1;
                }
                slots.push({ number, direction: 'across', row: r, col: c, length: len, answer });
            }
            if (startsDown) {
                let len = 0;
                let answer = '';
                while (isWhite(ch(r + len, c))) {
                    answer += ch(r + len, c);
                    len += 1;
                }
                slots.push({ number, direction: 'down', row: r, col: c, length: len, answer });
            }
        }
    }
    return { grid, slots };
}
/** Stable slot id used as the clue-map key, e.g. "1A" / "7D". */
export function slotId(s) {
    return `${s.number}${s.direction === 'across' ? 'A' : 'D'}`;
}
/**
 * Sort clues into the contract's required order: by number, across before down.
 */
function sortClues(clues) {
    return [...clues].sort((a, b) => {
        if (a.number !== b.number)
            return a.number - b.number;
        if (a.direction === b.direction)
            return 0;
        return a.direction === 'across' ? -1 : 1;
    });
}
/**
 * Assemble + validate a puzzle from a spec. Throws with a descriptive message
 * on any structural problem, missing/extra clue, or grid-rule violation, so a
 * malformed puzzle can never enter the corpus.
 */
export function buildPuzzle(spec) {
    const chars = parseLayout(spec.layout);
    const { grid, slots } = deriveSlots(chars);
    const rows = chars.length;
    const cols = chars[0].length;
    // Every slot needs exactly one clue; no leftover clue keys allowed.
    const provided = new Set(Object.keys(spec.clues));
    const clues = slots.map((s) => {
        const id = slotId(s);
        const text = spec.clues[id];
        if (text === undefined) {
            throw new Error(`${spec.id}: missing clue for slot ${id} (${s.answer})`);
        }
        if (text.trim() === '') {
            throw new Error(`${spec.id}: empty clue for slot ${id} (${s.answer})`);
        }
        provided.delete(id);
        return {
            number: s.number,
            direction: s.direction,
            clue: text,
            answer: s.answer,
            row: s.row,
            col: s.col,
            length: s.length,
        };
    });
    if (provided.size > 0) {
        throw new Error(`${spec.id}: clue keys with no matching slot: ${[...provided].sort().join(', ')}`);
    }
    const puzzle = {
        id: spec.id,
        size: spec.size,
        difficulty: spec.difficulty,
        rows,
        cols,
        grid,
        clues: sortClues(clues),
        ...(spec.title !== undefined ? { title: spec.title } : {}),
        ...(spec.attribution !== undefined ? { attribution: spec.attribution } : {}),
    };
    const problems = validatePuzzle(puzzle);
    const gridProblems = checkGridRules(puzzle);
    const all = [...problems, ...gridProblems];
    if (all.length > 0) {
        throw new Error(`${spec.id} failed validation:\n  - ${all.join('\n  - ')}`);
    }
    return puzzle;
}
/**
 * The American-grid rules that `validatePuzzle()` (structure-only) does not
 * enforce: 180° rotational symmetry of black squares, every white cell checked
 * (part of both an across and a down word), full connectivity of white cells,
 * and a black-square budget. Returns human-readable problems; empty = clean.
 *
 * Exported so the corpus test can assert it across every shipped puzzle.
 */
export function checkGridRules(p) {
    const problems = [];
    const { rows, cols, grid } = p;
    const block = (r, c) => grid[r]?.[c]?.block ?? true;
    const white = (r, c) => {
        const cell = grid[r]?.[c];
        return cell !== undefined && !cell.block;
    };
    // 180° rotational symmetry of the block pattern.
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            const mirror = block(rows - 1 - r, cols - 1 - c);
            if (block(r, c) !== mirror) {
                problems.push(`block pattern not 180°-symmetric at ${r},${c}`);
            }
        }
    }
    // Every white cell must be checked: part of an across word AND a down word
    // (i.e. have a white neighbour horizontally AND vertically). Combined with
    // the 3-letter minimum enforced via clues, this rules out unchecked cells.
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            if (!white(r, c))
                continue;
            const hasH = white(r, c - 1) || white(r, c + 1);
            const hasV = white(r - 1, c) || white(r + 1, c);
            if (!hasH)
                problems.push(`white cell ${r},${c} is not in an across word (unchecked)`);
            if (!hasV)
                problems.push(`white cell ${r},${c} is not in a down word (unchecked)`);
        }
    }
    // Minimum run length 3 in both directions (defence in depth alongside clue
    // length checks — catches stray 1/2-cell runs even before clues exist).
    for (let r = 0; r < rows; r += 1) {
        let run = 0;
        for (let c = 0; c <= cols; c += 1) {
            if (c < cols && white(r, c)) {
                run += 1;
            }
            else {
                if (run === 1 || run === 2)
                    problems.push(`across run of ${run} ending at ${r},${c - 1}`);
                run = 0;
            }
        }
    }
    for (let c = 0; c < cols; c += 1) {
        let run = 0;
        for (let r = 0; r <= rows; r += 1) {
            if (r < rows && white(r, c)) {
                run += 1;
            }
            else {
                if (run === 1 || run === 2)
                    problems.push(`down run of ${run} ending at ${r - 1},${c}`);
                run = 0;
            }
        }
    }
    // Full connectivity: all white cells form one orthogonally-connected region.
    const whites = [];
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1)
            if (white(r, c))
                whites.push([r, c]);
    }
    if (whites.length > 0) {
        const seen = new Set();
        const stack = [whites[0]];
        seen.add(`${whites[0][0]},${whites[0][1]}`);
        while (stack.length > 0) {
            const [r, c] = stack.pop();
            for (const [dr, dc] of [
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                const nr = r + dr;
                const nc = c + dc;
                const key = `${nr},${nc}`;
                if (white(nr, nc) && !seen.has(key)) {
                    seen.add(key);
                    stack.push([nr, nc]);
                }
            }
        }
        if (seen.size !== whites.length) {
            problems.push(`white cells are not fully connected (${seen.size}/${whites.length} reachable)`);
        }
    }
    // Black-square budget. The ~16% convention is a 15x15 rule of thumb; small
    // grids legitimately run denser (one block is a large fraction of the grid,
    // and published 5×5 "Mini" puzzles routinely carry 4–8 blocks). We therefore
    // scale the ceiling by size: Mini (<=25 cells) gets 28%, Midi (<=81) gets
    // 20%, full-size grids hold the 16% line.
    const total = rows * cols;
    const blocks = total - whites.length;
    const ratio = blocks / total;
    const ceiling = total <= 25 ? 0.28 : total <= 81 ? 0.2 : 0.16;
    if (ratio > ceiling) {
        problems.push(`black squares ${(ratio * 100).toFixed(1)}% exceed ${(ceiling * 100).toFixed(0)}% budget`);
    }
    return problems;
}
//# sourceMappingURL=build.js.map